import type { JobAcceptanceStatus, JobAssistStatus, JobSource, JobStatus } from '../types/database';
import { DEFAULT_TRIP_TIME_ZONE } from '../services/tripDeparture';

/** Client Portal visible request lifecycle (SOL-20260716-client-org-portal). */
export type ClientRequestStatus = 'submitted' | 'accepted' | 'scheduled' | 'rejected';

export function mapJobToClientRequestStatus(job: {
  status: JobStatus;
  source?: JobSource | null;
  acceptance_status?: JobAcceptanceStatus | null;
}): ClientRequestStatus {
  const acceptance = job.acceptance_status ?? 'n/a';
  if (acceptance === 'rejected') return 'rejected';
  if (job.status === 'cancelled' && job.source === 'client_request') return 'rejected';
  if (job.status === 'scheduled') return 'scheduled';
  if (acceptance === 'accepted' || job.status === 'planning') return 'accepted';
  return 'submitted';
}

export function formatClientRequestStatus(status: ClientRequestStatus): string {
  switch (status) {
    case 'submitted':
      return 'Submitted';
    case 'accepted':
      return 'Accepted';
    case 'scheduled':
      return 'Scheduled';
    case 'rejected':
      return 'Rejected';
    default:
      return status;
  }
}

export function getClientRequestStatusColor(
  status: ClientRequestStatus
): 'default' | 'processing' | 'success' | 'error' | 'warning' {
  switch (status) {
    case 'submitted':
      return 'processing';
    case 'accepted':
      return 'warning';
    case 'scheduled':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
}

/** Civil YYYY-MM-DD in an IANA time zone (default Australia/Sydney). */
export function civilDateInTimeZone(
  date: Date = new Date(),
  timeZone: string = DEFAULT_TRIP_TIME_ZONE
): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  return `${map.year}-${map.month}-${map.day}`;
}

/** Minutes from midnight in an IANA time zone. */
export function civilMinutesInTimeZone(
  date: Date = new Date(),
  timeZone: string = DEFAULT_TRIP_TIME_ZONE
): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date);
  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  return Number(map.hour) * 60 + Number(map.minute);
}

/**
 * True when scheduleDate + pickupMinutes (from midnight) is already past "now"
 * in the business time zone (default Australia/Sydney).
 */
export function isPastSchedulePickup(
  scheduleDateYmd: string,
  pickupMinutesFromMidnight: number,
  now: Date = new Date(),
  timeZone: string = DEFAULT_TRIP_TIME_ZONE
): boolean {
  if (!scheduleDateYmd?.trim() || !Number.isFinite(pickupMinutesFromMidnight)) return false;
  const today = civilDateInTimeZone(now, timeZone);
  if (scheduleDateYmd < today) return true;
  if (scheduleDateYmd > today) return false;
  return pickupMinutesFromMidnight < civilMinutesInTimeZone(now, timeZone);
}

/**
 * Unscheduled client request whose schedule_date is today or earlier (Sydney calendar day).
 * Used for client-facing reminder badges (date-only overdue).
 */
export function isClientRequestOverdueUnscheduled(
  job: {
    schedule_date: string;
    status: JobStatus;
    source?: JobSource | null;
    acceptance_status?: JobAcceptanceStatus | null;
  },
  now: Date = new Date(),
  timeZone: string = DEFAULT_TRIP_TIME_ZONE
): boolean {
  const status = mapJobToClientRequestStatus(job);
  if (status === 'scheduled' || status === 'rejected') return false;
  if (!job.schedule_date?.trim()) return false;
  const today = civilDateInTimeZone(now, timeZone);
  return job.schedule_date <= today;
}

/** Admin/ops: unscheduled or planning job whose travel date is today or earlier (Sydney). */
export function isJobOverdueUnscheduled(
  job: {
    schedule_date: string;
    status: JobStatus;
    assist_status?: JobAssistStatus | null;
  },
  now: Date = new Date(),
  timeZone: string = DEFAULT_TRIP_TIME_ZONE
): boolean {
  if (job.status !== 'unscheduled' && job.status !== 'planning') return false;
  // Fleet requested platform assist — not fleet's to schedule.
  if (job.assist_status === 'pending_platform') return false;
  if (!job.schedule_date?.trim()) return false;
  return job.schedule_date <= civilDateInTimeZone(now, timeZone);
}
